import React, { useState } from "react";
import DataTable from "../../../components/forms/DataTable";
import BatchBar from "../../../components/common/BatchBar";
import Panel from "../home/Panel";
import useTableColumns from "../../../hooks/useTableColumns";
import useSelection from "../../../hooks/useSelection";
import { toggleSort } from "../../../utils/sortRows";
import ExportDialog from "../../../components/common/ExportDialog";
import { exportWorkbook } from "../../../utils/tableExport";
import { TABLES, loadSort, saveSort, sortTableRows, tableSheet } from "./reportsConfig";
import styles from "./Reports.module.css";

// One report table: tickboxes, sort, column drag, resize and the + column menu; rows are calculated so there is no "Add a detail".
export default function ReportTable({ tableKey, report, fmt, onExported }) {
  const def = TABLES[tableKey];
  const { columns, toggleColumn, moveColumn } = useTableColumns(def.storageKey, def.columns);
  const selection = useSelection();
  const [sort, setSort] = useState(() => loadSort(def.storageKey));

  const rows = sortTableRows(def, def.rows(report), sort);
  const cells = Object.fromEntries(def.columns.map((c) => [c.key, (row) => (c.render ? c.render(row, fmt) : fmt.cell(c.format, row[c.key]))]));
  const total = def.footer && rows.length ? def.footer(rows) : null;
  const footer = total && Object.fromEntries(def.columns.map((c) => [c.key, total[c.key] == null ? "" : fmt.cell(c.format, total[c.key])]));

  const onSort = (key) => {
    const next = toggleSort(sort, key);
    setSort(next);
    saveSort(def.storageKey, next);
  };

  const [exporting, setExporting] = useState(false);
  const exportSelected = async ({ format }) => {
    const sheet = tableSheet(tableKey, report, selection.selectedIds);
    const kind = await exportWorkbook({ sheets: [sheet], filename: `${def.storageKey.replace(/Columns$/, "")}-selected`, format });
    onExported(sheet.rows.length, kind);
  };

  return (
    <Panel title={def.title} sub={typeof def.sub === "function" ? def.sub(report) : def.sub}>
      <BatchBar count={selection.selectedIds.size} onClear={selection.clear}>
        <button type="button" onClick={() => setExporting(true)}>Export selected</button>
      </BatchBar>
      {exporting && <ExportDialog title={`Export ${def.title.toLowerCase()}`} onExport={exportSelected} onClose={() => setExporting(false)} />}
      {rows.length === 0 ? (
        <p className={styles.empty}>Nothing to show for this period and these filters.</p>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          storageKey={def.storageKey}
          sortColumn={sort?.key}
          sortDirection={sort?.dir || "asc"}
          onSort={onSort}
          onToggleColumn={toggleColumn}
          onMoveColumn={moveColumn}
          selectable
          selectedIds={selection.selectedIds}
          getRowId={def.rowId}
          onToggleRow={selection.toggleRow}
          onToggleAll={(checked) => selection.toggleAll(rows.map(def.rowId), checked)}
          customCellRender={cells}
          footer={footer}
          maxRows={10}
        />
      )}
    </Panel>
  );
}
